import React, { useEffect, useState } from 'react';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import CartaoExpansivel from './CartaoExpansivel';
import AvisoDadosDesatualizados from './AvisoDadosDesatualizados';
import { formatarNumero } from '../../utils/formatters';
import { CONDICOES_AMBIENTAIS, MOTIVOS_INTERRUPCAO, rotuloDe } from '../../pages/Pescadores/InterrupcaoForm';

interface ItemDistribuicao {
  chave: string;
  dias: number;
  pct: number;
}

interface DadosInterrupcoes {
  totais: { pescadorDiasSemAtividade: number; pescadorDiasAmbientais: number; informantesAfetados: number };
  porMotivo: ItemDistribuicao[];
  porCondicao: ItemDistribuicao[];
  proporcao: { diasSemAtividade: number; diasComPesca: number; diasComPescaDentroDePeriodo: number; pct: number | null };
  periodosConsiderados: number;
  filtrosNaoAplicados: string[];
}

interface AtividadeInterrompidaProps {
  /** Query string dos filtros globais (sem '?'), a mesma do /pesca/stats. */
  filtrosQuery: string;
}

const COR_BARRA = '#00c3ff';
const COR_AMBIENTAL = '#f5a623';

const formatarPct = (pct: number) => (pct > 0 && pct < 1 ? '<1%' : `${formatarNumero(pct, pct < 10 ? 1 : 0)}%`);

const NOTA_FILTROS = 'Este bloco considera projeto, período, pescador, colônia e área. Espécie, petrecho, local e "somente validados" não se aplicam a períodos sem atividade.';

const Numero: React.FC<{ titulo: string; valor: string; unidade: string; detalhe?: React.ReactNode; dica?: string; carregando: boolean }> = ({ titulo, valor, unidade, detalhe, dica, carregando }) => (
  <div title={dica} style={{ flex: '1 1 220px', background: '#f7fafb', borderRadius: 10, padding: '12px 16px', opacity: carregando ? 0.6 : 1 }}>
    <div style={{ fontSize: '0.78rem', color: '#666', fontWeight: 600 }}>{titulo}</div>
    <div style={{ fontSize: '1.7rem', fontWeight: 700, color: '#0a6c8f', lineHeight: 1.2 }}>
      {valor} <small style={{ fontSize: '0.8rem', fontWeight: 500, color: '#777' }}>{unidade}</small>
    </div>
    {detalhe && <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 2 }}>{detalhe}</div>}
  </div>
);

const TooltipDistribuicao = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '8px 12px', fontSize: '0.8rem', color: '#444' }}>
      <strong>{g.rotulo}</strong><br />
      {formatarNumero(g.dias, 0)} pescador-dias ({formatarPct(g.pct)})
    </div>
  );
};

const BarrasDistribuicao: React.FC<{ titulo: string; itens: { rotulo: string; dias: number; pct: number }[]; cor: string; carregando: boolean; vazio: string }> = ({ titulo, itens, cor, carregando, vazio }) => {
  const dados = itens.map((i) => ({ ...i, value: i.pct, rotuloBarra: `${formatarPct(i.pct)} · ${formatarNumero(i.dias, 0)} dias` }));
  const altura = Math.max(150, dados.length * 30 + 40);
  return (
    <CartaoExpansivel titulo={titulo} alturaNormal={altura}>
      {(h) => {
        const expandido = typeof h === 'string';
        if (dados.length === 0) return <p style={{ padding: '20px', color: '#999' }}>{vazio}</p>;
        return (
          <div style={{ width: '100%', height: expandido ? `max(${h}, ${dados.length * 30 + 40}px)` : h, padding: '4px 20px 12px 10px', boxSizing: 'border-box', opacity: carregando ? 0.6 : 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={dados} margin={{ left: 0, right: 150 }}>
                <XAxis type="number" hide domain={[0, 'dataMax']} />
                <YAxis dataKey="rotulo" type="category" width={170} axisLine={false} tickLine={false} style={{ fontSize: '12px', fontWeight: 'bold' }} />
                <Tooltip cursor={{ fill: '#f1f4f6' }} content={<TooltipDistribuicao />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                  {dados.map((d) => <Cell key={d.rotulo} fill={cor} />)}
                  <LabelList dataKey="rotuloBarra" position="right" style={{ fontSize: '11px', fill: '#555' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }}
    </CartaoExpansivel>
  );
};

const AtividadeInterrompida: React.FC<AtividadeInterrompidaProps> = ({ filtrosQuery }) => {
  const [dados, setDados] = useState<DadosInterrupcoes | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0); // "Tentar de novo" refaz a busca sem mudar os filtros

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    api.get(`/pesca/stats/interrupcoes?${filtrosQuery}`)
      .then((res) => { if (ativo) { setDados(res.data.data); setErro(null); } })
      // mantém os dados anteriores na tela e avisa que estão desatualizados
      .catch((e) => { if (ativo) setErro(e?.response?.data?.message || 'Falha ao carregar a atividade interrompida.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [filtrosQuery, tentativa]);

  const semDados = !!dados && dados.periodosConsiderados === 0;
  const t = dados?.totais;
  const prop = dados?.proporcao;
  const ignorados = dados?.filtrosNaoAplicados ?? [];

  const dicaProporcao = prop
    ? `${formatarNumero(prop.diasSemAtividade, 0)} dias sem atividade ÷ (${formatarNumero(prop.diasComPesca, 0)} dias com pesca + ${formatarNumero(prop.diasSemAtividade, 0)} dias sem atividade), considerando só os informantes afetados.`
    : undefined;

  return (
    <div style={{ marginTop: '10px' }}>
      <AvisoDadosDesatualizados erro={!!erro} comDadosAnteriores={!!dados} detalhe={erro} carregando={carregando} onTentarDeNovo={() => setTentativa((x) => x + 1)} />

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>Atividade interrompida</div>
        <p style={{ margin: '0 20px 8px', fontSize: '0.78rem', color: '#777', lineHeight: 1.45 }}>
          Dias em que o informante ficou sem pescar (períodos sem atividade cadastrados). Um período em andamento conta até hoje.
          <br />
          <strong>{NOTA_FILTROS}</strong>
          {ignorados.length > 0 && <> <span style={{ color: '#8a5a00' }}>Filtros ativos ignorados aqui: {ignorados.join(', ')}.</span></>}
        </p>

        {semDados ? (
          <p style={{ padding: '10px 20px 20px', color: '#999', margin: 0 }}>Nenhum período sem atividade registrado para os filtros atuais.</p>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '0 20px 12px' }}>
              <Numero
                titulo="Dias sem atividade"
                valor={t ? formatarNumero(t.pescadorDiasSemAtividade, 0) : '–'}
                unidade="pescador-dias"
                dica={dicaProporcao}
                carregando={carregando}
                detalhe={prop && prop.pct != null
                  ? <>{formatarNumero(prop.pct, 1)}% dos dias dos informantes afetados (sem atividade ÷ com pesca + sem atividade)</>
                  : undefined}
              />
              <Numero
                titulo="Por condições ambientais"
                valor={t ? formatarNumero(t.pescadorDiasAmbientais, 0) : '–'}
                unidade="pescador-dias"
                carregando={carregando}
                detalhe={t && t.pescadorDiasSemAtividade > 0 ? <>{formatarNumero((t.pescadorDiasAmbientais / t.pescadorDiasSemAtividade) * 100, 0)}% dos dias sem atividade</> : undefined}
              />
              <Numero
                titulo="Informantes afetados"
                valor={t ? formatarNumero(t.informantesAfetados, 0) : '–'}
                unidade="informantes"
                carregando={carregando}
                detalhe={dados ? <>{formatarNumero(dados.periodosConsiderados, 0)} {dados.periodosConsiderados === 1 ? 'período' : 'períodos'}</> : undefined}
              />
            </div>
            {prop && prop.diasComPescaDentroDePeriodo > 0 && (
              <p style={{ margin: '0 20px 10px', fontSize: '0.75rem', color: '#8a5a00' }}>
                {formatarNumero(prop.diasComPescaDentroDePeriodo, 0)} {prop.diasComPescaDentroDePeriodo === 1 ? 'dia tem' : 'dias têm'} registro de pesca dentro de um período sem atividade e entram nas duas contas.
              </p>
            )}
          </>
        )}
      </div>

      {!semDados && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '10px' }}>
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>Por motivo</div>
            <BarrasDistribuicao
              titulo="Atividade interrompida · Por motivo"
              itens={(dados?.porMotivo ?? []).map((m) => ({ rotulo: rotuloDe(MOTIVOS_INTERRUPCAO, m.chave), dias: m.dias, pct: m.pct }))}
              cor={COR_BARRA}
              carregando={carregando}
              vazio="Sem dados."
            />
          </div>
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>Por condição ambiental <small>(só motivo ambiental)</small></div>
            <BarrasDistribuicao
              titulo="Atividade interrompida · Por condição ambiental"
              itens={(dados?.porCondicao ?? []).map((c) => ({ rotulo: rotuloDe(CONDICOES_AMBIENTAIS, c.chave), dias: c.dias, pct: c.pct }))}
              cor={COR_AMBIENTAL}
              carregando={carregando}
              vazio="Nenhum dia sem atividade por motivo ambiental."
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AtividadeInterrompida;
